<script setup lang="ts">
/**
 * Шапка встречи — макеты 12-3…12-6, 12-9Б/В/Г, 12-10 (эпик DEV-2088).
 *
 * Одна встреча за свою жизнь проходит через шесть состояний, и человек, открыв
 * её, первым делом спрашивает «а что сейчас». Поэтому состояние стоит рядом с
 * названием пилюлей, а всё, что из него следует — куда нажать, чего ждать, что
 * сломалось, — идёт полосами под ней: полоса объясняет и тут же даёт выход,
 * тогда как тост про то же самое исчезает раньше, чем его дочитают.
 *
 * Своей копии встречи экран не держит дольше действия: правка и отмена
 * возвращают новую строку, и она тут же заменяет показанную — иначе шапка
 * говорила бы о времени, которого уже нет.
 */
import { AppButton, AppModal, AppUserPicker, useToast } from '@vinprop/design-system'
import {
  CalendarPlus,
  ExternalLink,
  Pencil,
  Repeat,
  Send,
  Undo2,
  UserPlus,
  Video,
  X,
} from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { errorMessage } from '@/api/errors'
import { authApi } from '@/features/auth/api'
import { useProjectsStore } from '@/features/projects/store'

import { isMeetingDone, nextOccurrence, recurrenceLabel } from '../api'
import { peerColor } from '../collab'
import { useMeetingStore } from '../store'

import MeetingAnswerMark from './MeetingAnswerMark.vue'
import MeetingCancelDialog from './MeetingCancelDialog.vue'
import MeetingScheduleModal from './MeetingScheduleModal.vue'

import type {
  MeetingFormPreset,
  MeetingParticipant,
  MeetingSession,
  RescheduleMeetingInput,
  ScheduleMeetingInput,
} from '../api'
import type { Person } from '@/features/auth/types'

const props = defineProps<{ meeting: MeetingSession }>()

const { t, locale } = useI18n()
const toast = useToast()
const router = useRouter()
const store = useMeetingStore()
const projects = useProjectsStore()

/**
 * Показанная встреча. Копия, а не проп напрямую: перенос и повтор создания
 * комнаты отвечают новой строкой, и ждать ради неё перезагрузки экрана значит
 * оставить на нём старое время.
 */
const shown = ref<MeetingSession>(props.meeting)

watch(
  () => props.meeting,
  (meeting) => (shown.value = meeting),
)

const isLive = computed(() => shown.value.active)

const isDone = computed(() => isMeetingDone(shown.value))
const isScheduled = computed(() => !isLive.value && !isDone.value)
const isAuthor = computed(() => shown.value.iAmAuthor)

/**
 * Отменённый созвон возвращают на место (DEV-2193).
 *
 * Только тот, что ещё впереди: у прошедшего возвращать нечего — записи не
 * будет, а в списке он встанет встречей, которая уже не состоится.
 *
 * И только созвон живой серии: его событие остаётся в календаре убранным
 * вхождением, и Google возвращает его тем же идентификатором. У одиночной
 * встречи и у серии, отменённой «этой и следующими», события больше нет —
 * такую планируют снова, и кнопка ниже говорит об этом прямо (DEV-2200).
 */
const isCancelled = computed(
  () =>
    shown.value.meetingStatus === 'CANCELLED' &&
    shown.value.seriesActive &&
    !!shown.value.scheduledEnd &&
    new Date(shown.value.scheduledEnd) > new Date(),
)

/**
 * Конспект уже собран. Разбор такой встречи перезапускают: промпт правят, и
 * прогнать по нему ту же запись — единственный способ поменять результат
 * (DEV-2112). Запись лежит у нас, у Google ничего не переспрашиваем.
 */
const isAnalysed = computed(() => ['READY', 'QUALIFIED'].includes(shown.value.meetingStatus))

/**
 * Прошедшую встречу планируют снова (DEV-2200): отменённую, состоявшуюся,
 * без записи, со сбоем — любую, у которой созвона впереди уже нет. У живой
 * серии он есть, и «снова» там значило бы вторую параллельную серию; у
 * отменённой, но ещё не наступившей, — дубль того, что можно просто вернуть.
 */
const canReplan = computed(
  () =>
    isDone.value &&
    isAuthor.value &&
    !isLive.value &&
    !isCancelled.value &&
    !shown.value.seriesActive,
)

/** Ряд действий: ссылка на комнату всем, остальное — автору встречи. */
const hasActions = computed(
  () =>
    !!shown.value.joinUrl ||
    (isAuthor.value &&
      (isScheduled.value ||
        isLive.value ||
        isAnalysed.value ||
        isCancelled.value ||
        canReplan.value)),
)

/** Ад-хок встреча: началась приглашением, комнаты у неё нет (макет 12-10) */
const isWithoutMeet = computed(() => !shown.value.meetingId)

/**
 * Состояние пилюли. Идущая с комнатой подписана записью: запись включена по
 * умолчанию (решение 6 гриля), и человек имеет право знать об этом до того,
 * как заговорит.
 */
const badge = computed<{ label: string; tone: string }>(() => {
  const meeting = shown.value

  if (meeting.active) {
    return meeting.meetingId
      ? { label: t('meetings.status.recording'), tone: 'live' }
      : { label: t('meetings.status.live'), tone: 'live' }
  }

  switch (meeting.meetingStatus) {
    case 'CREATING':
      return { label: t('meetings.status.creating'), tone: 'muted' }
    case 'CREATE_FAILED':
      return { label: t('meetings.status.createFailed'), tone: 'error' }
    case 'ENDED':
    case 'PROCESSING':
      return { label: t('meetings.status.processing'), tone: 'muted' }
    case 'READY':
    case 'QUALIFIED':
      return { label: t('meetings.status.ready'), tone: 'ready' }
    case 'NO_RECORDING':
      return { label: t('meetings.status.noRecording'), tone: 'warn' }
    case 'FAILED':
      return { label: t('meetings.status.failed'), tone: 'error' }
    case 'CANCELLED':
      return { label: t('meetings.status.cancelled'), tone: 'muted' }
    default:
      return isScheduled.value
        ? { label: t('meetings.status.scheduled'), tone: 'muted' }
        : { label: t('meetings.status.ended'), tone: 'muted' }
  }
})

const when = computed(() => {
  const meeting = shown.value

  if (!meeting.scheduledStart) return ''

  const start = new Date(meeting.scheduledStart)
  const date = start.toLocaleString(locale.value, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

  if (!meeting.scheduledEnd) return date

  const end = new Date(meeting.scheduledEnd).toLocaleTimeString(locale.value, {
    hour: '2-digit',
    minute: '2-digit',
  })

  return `${date} — ${end}`
})

const projectName = computed(
  () => projects.items.find((project) => project.id === shown.value.projectId)?.name ?? '',
)

const hasMailbox = (participant: MeetingParticipant) => participant.hasMailbox

/** Ответ на приглашение — подсказкой у имени; автору отвечать не на что */
const responseHint = (participant: MeetingParticipant): string => {
  if (participant.userId === shown.value.authorId) return ''
  if (!participant.hasMailbox) return t('meetings.invites.noMailbox')

  switch (participant.responseStatus) {
    case 'accepted':
      return t('meetings.invites.accepted')
    case 'declined':
      return t('meetings.invites.declined')
    case 'tentative':
      return t('meetings.invites.tentative')
    default:
      return t('meetings.invites.waiting')
  }
}

/**
 * Значок ответа — пока созвон впереди и человеку есть куда ответить. Автору
 * отвечать не на что: событие стоит в его календаре как организатора.
 */
const showsAnswer = (participant: MeetingParticipant): boolean =>
  Boolean(shown.value.meetingId) &&
  !isDone.value &&
  participant.hasMailbox &&
  participant.userId !== shown.value.authorId

/**
 * Переслать приглашение одному — тому, кто ещё не принял: у принявшего письмо
 * есть, а новое лишь сбросило бы его ответ (DEV-2208).
 */
const canResendTo = (participant: MeetingParticipant): boolean =>
  isAuthor.value &&
  showsAnswer(participant) &&
  participant.userId !== shown.value.authorId &&
  participant.responseStatus !== 'accepted'

/** Счётчики полосы — без автора: он организатор, а не приглашённый */
const invites = computed(() => {
  const people = shown.value.participants.filter((person) => person.userId !== shown.value.authorId)

  return {
    accepted: people.filter((person) => person.responseStatus === 'accepted').length,
    waiting: people.filter((person) => person.hasMailbox && person.responseStatus !== 'accepted')
      .length,
    noMailbox: people.filter((person) => !person.hasMailbox).length,
  }
})

/**
 * Полосы под шапкой: что происходит и что с этим делать.
 *
 * Списком, а не набором `v-if` в разметке: полос у одного состояния бывает
 * две — идущий созвон и так, и без записи, — и порядок между ними должен быть
 * один и тот же, а не тот, в каком их когда-то дописали.
 */
interface Bar {
  key: string
  tone: string
  text: string
}

const bars = computed<Bar[]>(() => {
  const meeting = shown.value
  const list: Bar[] = []

  if (meeting.meetingId && !isDone.value && meeting.participants.length) {
    list.push({
      key: 'invites',
      tone: 'muted',
      text: t('meetings.bars.invites', invites.value),
    })
  }

  if (meeting.meetingStatus === 'CREATING') {
    list.push({ key: 'creating', tone: 'muted', text: t('meetings.bars.creating') })
  }

  if (meeting.meetingStatus === 'CREATE_FAILED') {
    list.push({
      key: 'createFailed',
      tone: 'error',
      text: meeting.failureReason || t('meetings.bars.createFailed'),
    })
  }

  if (isLive.value && meeting.meetingId) {
    list.push({ key: 'recording', tone: 'live', text: t('meetings.bars.recording') })
  }

  if (isLive.value && isWithoutMeet.value) {
    list.push({ key: 'noMeet', tone: 'muted', text: t('meetings.bars.noMeet') })
  }

  if (['ENDED', 'PROCESSING'].includes(meeting.meetingStatus)) {
    list.push({ key: 'processing', tone: 'muted', text: t('meetings.bars.processing') })
  }

  if (meeting.meetingStatus === 'NO_RECORDING') {
    list.push({ key: 'noRecording', tone: 'warn', text: t('meetings.bars.noRecording') })
  }

  if (meeting.meetingStatus === 'FAILED') {
    list.push({
      key: 'failed',
      tone: 'error',
      text: meeting.failureReason || t('meetings.bars.failed'),
    })
  }

  return list
})

/**
 * Позвать ещё людей. У названной встречи состав уезжает в attendees события
 * Google — приглашение с ссылкой и .ics рассылает календарь, а не трекер.
 */
const people = ref<Person[]>([])
const isDirectoryOpen = ref(true)
const invitee = ref('')
const pickerKey = ref(0)

const loadPeople = async () => {
  if (people.value.length || !isDirectoryOpen.value) return

  try {
    people.value = await authApi.listUsers()
  } catch {
    isDirectoryOpen.value = false
  }
}

const invite = async (userId: string) => {
  if (!userId) return

  invitee.value = ''
  pickerKey.value += 1

  const updated = await store.inviteTo(shown.value.id, [userId])

  if (updated) shown.value = updated
}

const isBusy = ref(false)

/**
 * Убрать из состава (DEV-2129). Кнопка стоит только у того, кого убрать
 * действительно можно: автора не убирают, в законченную встречу состав уже не
 * правят, а тот, кто сидел в звонке, остаётся — он звучит в расшифровке, и
 * список участников должен совпадать с тем, кто говорил.
 *
 * Остальные отказы видно только бэкенду (человек писал заметки), поэтому в
 * тосте его текст, а не общее «не получилось».
 */
const canRemove = (participant: MeetingParticipant): boolean =>
  isAuthor.value &&
  !isDone.value &&
  participant.userId !== shown.value.authorId &&
  !participant.joinedCall

const removing = ref<MeetingParticipant | null>(null)

const remove = async () => {
  const target = removing.value

  if (!target) return

  isBusy.value = true

  try {
    shown.value = await store.removeFrom(shown.value.id, target.userId)
    removing.value = null
    toast.success(t('meetings.participants.removed', { name: target.name || '…' }))
  } catch (failure) {
    toast.error(errorMessage(failure, t('meetings.participants.removeFailed')))
  } finally {
    isBusy.value = false
  }
}

/** Завести комнату заново после отказа Google — кнопка на полосе сбоя */
const retryRoom = async () => {
  isBusy.value = true

  try {
    shown.value = await store.retryRoom(shown.value.id)
  } catch {
    toast.error(t('meetings.actions.retryFailed'))
  } finally {
    isBusy.value = false
  }
}

/**
 * Прогнать запись через разбор заново — кнопка на полосе сбоя обработки и на
 * готовой встрече.
 *
 * Полосу видят все участники, кнопку — автор: распоряжается встречей он, и
 * meetings примет повтор только от него.
 */
const retryProcessing = async () => {
  isBusy.value = true

  try {
    shown.value = await store.retryProcessing(shown.value.id)
  } catch {
    toast.error(t('meetings.actions.retryProcessingFailed'))
  } finally {
    isBusy.value = false
  }
}

/**
 * «Начать Meet» на ад-хок встрече: комната появляется у уже идущего разговора.
 *
 * Тема берётся у встречи, а спрашивать её заново нечего — приглашение в
 * календарь и так уйдёт с ней.
 */
const attachMeet = async () => {
  isBusy.value = true

  try {
    shown.value = await store.attachRoom(
      shown.value.id,
      shown.value.title || t('meetings.item.title'),
      true,
    )
    toast.success(t('meetings.actions.meetStarted'))
  } catch {
    toast.error(t('meetings.actions.meetFailed'))
  } finally {
    isBusy.value = false
  }
}

const endMeeting = async () => {
  if (!window.confirm(t('meetings.session.endConfirm'))) return

  await store.end()
}

/**
 * Серия — один разговор, растянутый на недели: «каждый понедельник» здесь
 * собирается из времени самого экземпляра. Своей записи о правиле повтора у
 * трекера нет, а показывать её всё равно негде, кроме этой строки (DEV-2097).
 */
const seriesRule = computed(() => {
  const meeting = shown.value

  if (!meeting.seriesRootId && !meeting.seriesId) return ''

  // Правило приезжает с бэкенда копией RRULE (DEV-2178). До него шапка
  // реконструировала «Каждый {weekday}» из даты экземпляра и врала на
  // ежедневных и произвольных правилах.
  return recurrenceLabel(meeting.recurrence, meeting.scheduledStart, t)
})

/** Длительность в минутах: правка и повтор наследуют её, а не полчаса */
const durationMinutes = computed(() => {
  const meeting = shown.value

  if (!meeting.scheduledStart || !meeting.scheduledEnd) return 30

  return Math.round(
    (new Date(meeting.scheduledEnd).getTime() - new Date(meeting.scheduledStart).getTime()) /
      60_000,
  )
})

/**
 * Правка встречи (DEV-2200) — та же форма, что и планирование: тема, время,
 * длительность, повтор; у серии — область. Одиночная с выбранным повтором
 * становится серией, и это единственный способ её такой сделать.
 */
const isEditing = ref(false)
const editForm = ref<InstanceType<typeof MeetingScheduleModal> | null>(null)

const editPreset = computed<MeetingFormPreset>(() => ({
  title: shown.value.title,
  start: new Date(shown.value.scheduledStart || Date.now()),
  durationMinutes: durationMinutes.value,
  projectId: shown.value.projectId,
  recurrence: shown.value.recurrence,
  // По серии в meetings, а не по корню истории: у одиночной, заведённой через
  // «Запланировать снова», корень есть, а серии нет — и области у неё нет.
  isSeries: !!shown.value.seriesId,
}))

const save = async (input: RescheduleMeetingInput) => {
  isBusy.value = true

  try {
    shown.value = await store.reschedule(shown.value.id, input)
    isEditing.value = false
    toast.success(t('meetings.actions.saved'))
  } catch (failure) {
    editForm.value?.stopSaving()
    // Причины отказа у бэкенда внятные («повтор доступен только у встречи с
    // комнатой Meet») — его слова полезнее общего «не получилось».
    toast.error(errorMessage(failure, t('meetings.actions.saveFailed')))
  } finally {
    isBusy.value = false
  }
}

/**
 * «Запланировать снова» — форма планирования, предзаполненная прошедшей
 * встречей. Созвон серии продолжает её: новая встреча ложится в историю той
 * же серии, и она снова живая.
 */
const isReplanning = ref(false)
const replanForm = ref<InstanceType<typeof MeetingScheduleModal> | null>(null)

const replanPreset = computed<MeetingFormPreset>(() => {
  const meeting = shown.value
  const now = new Date()
  const from = new Date(meeting.scheduledStart || now)

  // Следующая по правилу, но не раньше завтра: у серии, закончившейся месяц
  // назад, «следующая» давно прошла, а планировать в прошлое нечего.
  if (from < now) {
    from.setFullYear(now.getFullYear(), now.getMonth(), now.getDate())
  }

  return {
    title: meeting.title,
    start: nextOccurrence(meeting.recurrence, from),
    durationMinutes: durationMinutes.value,
    projectId: meeting.projectId,
    recurrence: meeting.recurrence,
    participants: meeting.participants
      .filter((participant) => participant.userId !== meeting.authorId)
      .map((participant) => ({
        id: participant.userId,
        name: participant.name,
        hasMailbox: participant.hasMailbox,
      })),
    seriesRootId: meeting.seriesRootId || meeting.seriesId || undefined,
  }
})

const replan = async (input: ScheduleMeetingInput) => {
  try {
    const meeting = await store.schedule(input)

    isReplanning.value = false
    toast.success(t('meetings.schedule.done'))
    await router.push(`/meetings/${meeting.id}`)
  } catch {
    replanForm.value?.stopSaving()
    toast.error(t('meetings.schedule.failed'))
  }
}

/** Отмена — своей модалкой с причиной; ею же панель отменяет серию */
const isCancelling = ref(false)

const cancelled = () => {
  shown.value = { ...shown.value, meetingStatus: 'CANCELLED' }
  isCancelling.value = false
}

/**
 * Вернуть отменённый созвон.
 *
 * Отказ показываем словами бэкенда: причин у него несколько — серия закрыта,
 * событие удалено целиком, созвон уже прошёл — и общее «не получилось»
 * оставило бы человека гадать, какая из них его.
 */
const restore = async () => {
  isBusy.value = true

  try {
    shown.value = await store.restore(shown.value.id)
    toast.success(t('meetings.actions.restored'))
  } catch (failure) {
    toast.error(errorMessage(failure, t('meetings.actions.restoreFailed')))
  } finally {
    isBusy.value = false
  }
}

/** Переслать приглашение одному: тост с именем, а не со счётчиком */
const resendTo = async (participant: MeetingParticipant) => {
  isBusy.value = true

  try {
    shown.value = await store.resendInvites(shown.value.id, [participant.userId])
    toast.success(t('meetings.participants.resent', { name: participant.name || '…' }))
  } catch (failure) {
    toast.error(errorMessage(failure, t('meetings.actions.resendFailed')))
  } finally {
    isBusy.value = false
  }
}

/** Позвать на встречу ещё раз всех, кто уже позван */
const resendInvites = async () => {
  isBusy.value = true

  try {
    shown.value = await store.resendInvites(shown.value.id)
    toast.success(t('meetings.actions.invitesResent', { count: shown.value.participants.length }))
  } catch (failure) {
    toast.error(errorMessage(failure, t('meetings.actions.resendFailed')))
  } finally {
    isBusy.value = false
  }
}
</script>

<template>
  <header class="mhead">
    <div class="mhead__top">
      <Repeat v-if="shown.seriesId" :size="16" class="mhead__kind" aria-hidden="true" />
      <Video v-else-if="shown.meetingId" :size="16" class="mhead__kind" aria-hidden="true" />

      <h2 class="mhead__title">{{ shown.title || t('meetings.item.title') }}</h2>

      <span class="mhead__badge" :class="`mhead__badge--${badge.tone}`">{{ badge.label }}</span>
    </div>

    <p v-if="when || projectName || seriesRule" class="mhead__meta">
      <span v-if="when">{{ when }}</span>
      <span v-if="seriesRule" class="mhead__series">{{ seriesRule }}</span>
      <span v-if="projectName" class="mhead__project">{{ projectName }}</span>
    </p>

    <!-- Участники: пришедшие цветом каретки, позванные приглушённо, без
         рабочего ящика — с пометкой: приглашения в календаре им не будет. -->
    <ul v-if="shown.participants.length || !isDone" class="mhead__people">
      <li
        v-for="participant in shown.participants"
        :key="participant.userId"
        class="mhead__person"
        :class="{ 'mhead__person--waiting': !participant.joined }"
        :title="responseHint(participant)"
      >
        <span
          v-if="participant.joined"
          class="mhead__dot"
          :style="{ background: peerColor(participant.userId) }"
        />
        {{ participant.name || '…' }}
        <MeetingAnswerMark v-if="showsAnswer(participant)" :status="participant.responseStatus" />
        <span v-if="!hasMailbox(participant)" class="mhead__mark">
          {{ t('meetings.schedule.noMailboxMark') }}
        </span>

        <button
          v-if="canResendTo(participant)"
          type="button"
          class="mhead__resend"
          :disabled="isBusy"
          :title="t('meetings.participants.resend')"
          :aria-label="t('meetings.participants.resend')"
          @click="resendTo(participant)"
        >
          <Send :size="12" aria-hidden="true" />
        </button>

        <button
          v-if="canRemove(participant)"
          type="button"
          class="mhead__remove"
          :title="t('meetings.participants.remove')"
          :aria-label="t('meetings.participants.remove')"
          @click="removing = participant"
        >
          <X :size="12" aria-hidden="true" />
        </button>
      </li>

      <!-- Звать можно, пока встреча не кончилась: в законченную звать некуда. -->
      <li v-if="!isDone" class="mhead__invite">
        <UserPlus :size="14" class="mhead__invite-icon" aria-hidden="true" />

        <AppUserPicker
          :key="pickerKey"
          v-model="invitee"
          :users="people"
          :placeholder="t('meetings.session.invite')"
          :empty-text="t('meetings.session.nobody')"
          :unavailable="!isDirectoryOpen"
          :unavailable-text="t('meetings.session.noDirectory')"
          @click="loadPeople()"
          @update:model-value="invite"
        />
      </li>
    </ul>

    <div v-if="bars.length" class="mhead__bars">
      <p v-for="bar in bars" :key="bar.key" class="mbar" :class="`mbar--${bar.tone}`">
        <span class="mbar__text">{{ bar.text }}</span>

        <button
          v-if="bar.key === 'createFailed'"
          type="button"
          class="mbar__action"
          :disabled="isBusy"
          @click="retryRoom()"
        >
          {{ t('meetings.actions.retryRoom') }}
        </button>

        <button
          v-if="bar.key === 'failed' && isAuthor"
          type="button"
          class="mbar__action"
          :disabled="isBusy"
          @click="retryProcessing()"
        >
          {{ t('meetings.actions.retryProcessing') }}
        </button>

        <button
          v-if="bar.key === 'noMeet' && isAuthor"
          type="button"
          class="mbar__action"
          :disabled="isBusy"
          @click="attachMeet()"
        >
          {{ t('meetings.actions.startMeet') }}
        </button>
      </p>
    </div>

    <div v-if="hasActions" class="mhead__actions">
      <a
        v-if="shown.joinUrl && !isDone"
        class="mhead__join"
        :href="shown.joinUrl"
        target="_blank"
        rel="noopener"
      >
        <Video :size="14" aria-hidden="true" />
        {{ isLive ? t('meetings.actions.openMeet') : t('meetings.actions.join') }}
      </a>

      <template v-if="isScheduled && isAuthor">
        <button type="button" class="mhead__action" :disabled="isBusy" @click="isEditing = true">
          <Pencil :size="13" aria-hidden="true" />
          {{ t('meetings.actions.edit') }}
        </button>

        <!-- Позвать заново: письмо теряется, и до сих пор единственным способом
             отправить его ещё раз было снять человека и позвать обратно — то
             есть сперва прислать ему отмену. -->
        <button type="button" class="mhead__action" :disabled="isBusy" @click="resendInvites()">
          {{ t('meetings.actions.resendInvites') }}
        </button>

        <button type="button" class="mhead__action" @click="isCancelling = true">
          {{ t('meetings.actions.cancel') }}
        </button>
      </template>

      <!-- Прошедшую планируют снова там же, где у будущей стоит «Изменить»:
           это главное, что можно сделать с созвоном, которого уже не будет. -->
      <button
        v-if="canReplan"
        type="button"
        class="mhead__action mhead__action--main"
        :disabled="isBusy"
        @click="isReplanning = true"
      >
        <CalendarPlus :size="13" aria-hidden="true" />
        {{ t('meetings.actions.replan') }}
      </button>

      <!-- Возврат стоит там же, где была отмена: передумавший ищет кнопку в том
           месте, где нажал предыдущую. -->
      <button
        v-if="isCancelled && isAuthor"
        type="button"
        class="mhead__action mhead__action--main"
        :disabled="isBusy"
        @click="restore()"
      >
        <Undo2 :size="13" aria-hidden="true" />
        {{ t('meetings.actions.restore') }}
      </button>

      <button v-if="isLive && isAuthor" type="button" class="mhead__action" @click="endMeeting()">
        {{ t('meetings.session.end') }}
      </button>

      <button
        v-if="isAnalysed && isAuthor"
        type="button"
        class="mhead__action"
        :disabled="isBusy"
        @click="retryProcessing()"
      >
        {{ t('meetings.actions.reanalyze') }}
      </button>

      <!-- Ссылка на запись в Диске здесь не показывается: файл лежит под ACL
           Google, и участники его не откроют. Слушают аудио — под расшифровкой. -->
      <a
        v-if="shown.joinUrl && isDone"
        class="mhead__action"
        :href="shown.joinUrl"
        target="_blank"
        rel="noopener"
      >
        <ExternalLink :size="13" aria-hidden="true" />
        {{ t('meetings.actions.room') }}
      </a>
    </div>
  </header>

  <MeetingScheduleModal
    v-if="isEditing"
    ref="editForm"
    mode="edit"
    :preset="editPreset"
    @close="isEditing = false"
    @save="save"
  />

  <MeetingScheduleModal
    v-if="isReplanning"
    ref="replanForm"
    :preset="replanPreset"
    @close="isReplanning = false"
    @submit="replan"
  />

  <MeetingCancelDialog
    v-if="isCancelling"
    :meeting="shown"
    @close="isCancelling = false"
    @cancelled="cancelled()"
  />

  <!-- Убрать участника спрашивают подтверждением: человек перестаёт видеть
       заметки, а в календаре ему уходит отмена — отменить это одним кликом
       обратно нельзя, звать придётся заново. -->
  <AppModal
    v-if="removing"
    model-value
    :title="t('meetings.participants.removeTitle')"
    size="small"
    @update:model-value="removing = null"
  >
    <p class="mhead__cancel">
      {{ t('meetings.participants.removeBody', { name: removing.name || '…' }) }}
    </p>

    <template #footer>
      <AppButton variant="secondary" @click="removing = null">
        {{ t('common.actions.cancel') }}
      </AppButton>

      <AppButton :disabled="isBusy" @click="remove()">
        {{ t('meetings.participants.removeSubmit') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.mhead {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mhead__top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mhead__kind {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.mhead__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: 20px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.2px;
  line-height: 26px;
}

.mhead__badge {
  padding: 1px 8px;
  border-radius: var(--radius-pill, 999px);
  font-size: var(--font-size-overline);
  line-height: 18px;
}

.mhead__badge--muted {
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
}

.mhead__badge--live {
  background: var(--color-success-tint, var(--color-primary-tint));
  color: var(--color-success-strong, var(--color-primary));
}

.mhead__badge--ready {
  background: var(--color-primary-tint);
  color: var(--color-primary);
}

.mhead__badge--warn {
  background: var(--color-warning-tint, var(--color-bg-muted));
  color: var(--color-warning-strong, var(--color-text-secondary));
}

.mhead__badge--error {
  background: var(--color-danger-tint, var(--color-bg-muted));
  color: var(--color-danger, var(--color-text-secondary));
}

.mhead__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
  line-height: 18px;
}

.mhead__project {
  padding: 0 6px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
}

.mhead__people {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.mhead__person {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--color-text-primary);
  font-size: var(--font-size-caption);
  line-height: 18px;
}

/* Позванный, но не пришедший, — приглушённо: иначе пригласивший не видит
   разницы между «человек здесь» и «человек ещё не открыл». */
.mhead__person--waiting {
  color: var(--color-text-muted);
}

.mhead__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

/* Приглашение — своей строкой под составом: рядом с именами иконка «позвать»
   читалась продолжением последнего участника, а не подписью поля. */
.mhead__invite {
  display: flex;
  flex-basis: 100%;
  align-items: center;
  gap: 6px;
}

.mhead__invite-icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.mhead__invite > :not(.mhead__invite-icon) {
  flex: 1;
  max-width: 240px;
}

/* Крестик приглушён до наведения: строка состава читается именами, а не
   рядом кнопок, — но на тач-экранах наведения нет, поэтому он не прячется. */
.mhead__remove,
.mhead__resend {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.mhead__remove:hover {
  color: var(--color-danger, var(--color-text-primary));
}

.mhead__resend:hover {
  color: var(--color-text-primary);
}

.mhead__resend:disabled {
  cursor: default;
  opacity: 0.5;
}

.mhead__mark {
  color: var(--color-warning-strong, var(--color-text-muted));
  font-size: var(--font-size-overline);
}

.mhead__bars {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 7px 10px;
  border-radius: var(--radius-md);
  font-size: var(--font-size-caption);
  line-height: 18px;
}

.mbar__text {
  flex: 1;
}

.mbar--muted {
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
}

.mbar--live {
  background: var(--color-success-tint, var(--color-primary-tint));
  color: var(--color-success-strong, var(--color-primary));
}

.mbar--warn {
  background: var(--color-warning-tint, var(--color-bg-muted));
  color: var(--color-warning-strong, var(--color-text-secondary));
}

.mbar--error {
  background: var(--color-danger-tint, var(--color-bg-muted));
  color: var(--color-danger, var(--color-text-secondary));
}

.mbar__action {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font-family: inherit;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  text-decoration: underline;
  cursor: pointer;
}

.mhead__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.mhead__join {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: var(--radius-md);
  background: var(--color-primary);
  color: var(--color-text-inverse, #fff);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  line-height: 18px;
  text-decoration: none;
}

.mhead__action {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  line-height: 18px;
  text-decoration: none;
  cursor: pointer;
}

.mhead__action:hover {
  color: var(--color-text-primary);
}

/* Возврат — единственное действие у отменённой встречи, и читается он как
   главное: остальные кнопки на ней не показываются вовсе. */
.mhead__action--main {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.mhead__action:disabled {
  opacity: 0.6;
  cursor: default;
}

.mhead__series {
  padding: 0 6px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
}

.mhead__cancel {
  margin: 0 0 12px;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  line-height: 20px;
}
</style>
